/**
 * When RAM runs inside a Cockpit iframe, ask the parent to navigate its Vue Router
 * (native contract detail, or addenda embed with a deep RAM path via query).
 */

import { getCockpitParentPostMessageTargetOrigin } from './cockpit-embed-origins';
import { isRamEmbeddedInCockpit } from './is-embedded';

export { isRamEmbeddedInCockpit } from './is-embedded';

export const RAM_TO_COCKPIT_MESSAGE_SOURCE = 'condo-ram' as const;
export const RAM_TO_COCKPIT_MESSAGE_TYPE = 'cockpit-navigate' as const;
/** Mirror RAM's in-iframe query string onto the parent Cockpit address bar (no navigation/reload). */
export const RAM_TO_COCKPIT_SYNC_QUERY_TYPE = 'cockpit-sync-query' as const;
/** Let the embedded page take over Cockpit's viewport (fullscreen cost tracking, sheet workspace, …). */
export const RAM_TO_COCKPIT_FULLSCREEN_TYPE = 'cockpit-fullscreen' as const;
/** An embedded modal page (Cockpit overlays it on its viewport) is on screen and can take the pointer. */
export const RAM_TO_COCKPIT_MODAL_READY_TYPE = 'cockpit-modal-ready' as const;
/** An embedded modal page closed; `succeeded` tells Cockpit to refresh what the modal changed. */
export const RAM_TO_COCKPIT_MODAL_CLOSED_TYPE = 'cockpit-modal-closed' as const;

export interface RamToCockpitNavigateRoute {
    name?: string;
    path?: string;
    params?: Record<string, string | number>;
    query?: Record<string, string>;
}

export function requestCockpitNavigation(route: RamToCockpitNavigateRoute): void {
    if (!isRamEmbeddedInCockpit()) {
        return;
    }
    const targetOrigin = getCockpitParentPostMessageTargetOrigin();
    if (!targetOrigin) {
        return;
    }
    window.parent.postMessage(
        {
            source: RAM_TO_COCKPIT_MESSAGE_SOURCE,
            type: RAM_TO_COCKPIT_MESSAGE_TYPE,
            route,
        },
        targetOrigin,
    );
}

/**
 * Ask the parent Cockpit to reflect RAM's current query string in its address bar so the embedded page
 * stays shareable/bookmarkable. `search` is RAM's `location.search` without the leading `?`. Cockpit
 * updates the query only (via `router.replace`), so no navigation, scroll, or iframe reload occurs.
 */
export function syncCockpitAddressBarSearch(search: string): void {
    if (!isRamEmbeddedInCockpit()) {
        return;
    }
    const targetOrigin = getCockpitParentPostMessageTargetOrigin();
    if (!targetOrigin) {
        return;
    }
    window.parent.postMessage(
        {
            source: RAM_TO_COCKPIT_MESSAGE_SOURCE,
            type: RAM_TO_COCKPIT_SYNC_QUERY_TYPE,
            search,
        },
        targetOrigin,
    );
}

/**
 * Ask the parent Cockpit to lift its RAM iframe out of the page flow so the embedded page owns the whole
 * viewport (Cockpit navbar, entity sidebar and summary header stay mounted but are covered). RAM hides its
 * own chrome independently — see `AssetDetailLayout`'s `isWorkspaceExpanded` branch.
 */
export function requestCockpitEmbedFullscreen(isFullscreen: boolean): void {
    if (!isRamEmbeddedInCockpit()) {
        return;
    }
    const targetOrigin = getCockpitParentPostMessageTargetOrigin();
    if (!targetOrigin) {
        return;
    }
    window.parent.postMessage(
        {
            source: RAM_TO_COCKPIT_MESSAGE_SOURCE,
            type: RAM_TO_COCKPIT_FULLSCREEN_TYPE,
            isFullscreen,
        },
        targetOrigin,
    );
}

function postModalMessageToCockpit(
    message: { type: typeof RAM_TO_COCKPIT_MODAL_READY_TYPE } | { type: typeof RAM_TO_COCKPIT_MODAL_CLOSED_TYPE; succeeded: boolean },
): void {
    if (!isRamEmbeddedInCockpit()) {
        return;
    }
    const targetOrigin = getCockpitParentPostMessageTargetOrigin();
    if (!targetOrigin) {
        return;
    }
    window.parent.postMessage({ source: RAM_TO_COCKPIT_MESSAGE_SOURCE, ...message }, targetOrigin);
}

/** Tell Cockpit the embedded modal is rendered, so it can drop its loading overlay and show the iframe. */
export function notifyCockpitEmbedModalReady(): void {
    postModalMessageToCockpit({ type: RAM_TO_COCKPIT_MODAL_READY_TYPE });
}

/** Tell Cockpit the embedded modal closed, so it removes the iframe (and refreshes its data when `succeeded`). */
export function notifyCockpitEmbedModalClosed(succeeded: boolean): void {
    postModalMessageToCockpit({ type: RAM_TO_COCKPIT_MODAL_CLOSED_TYPE, succeeded });
}
